import { useEffect, useRef, useState } from 'react';
import { Step } from '../types';

type StepperProps = {
    steps: Step[];
    defaultStepIndex?: number;
    onStepChange?: (currentStep: Step) => void;
    onFinalStepChange?: (currentStep: Step) => void;
};

export type Stepper = {
    currentStep: Step;
    currentStepIndex: number;
    isFirstStep: boolean;
    isLastStep: boolean;
    isValidStep: boolean;
    goToPreviousStep: () => void;
    goToNextStep: () => void;
    goToStep: (stepIndex: number) => void;
    completeFinalStep: () => void;
    setIsValidStep: (state: boolean) => void;
};

const useStepper: (props: StepperProps) => Stepper = ({
                                                          steps,
                                                          defaultStepIndex = 0,
                                                          onStepChange,
                                                          onFinalStepChange
                                                      }: StepperProps) => {
    const [currentStepIndex, setCurrentStepIndex] = useState<number>(defaultStepIndex);
    const [isValidStep, setIsValidStep] = useState<boolean>(true);
    const [currentStep, setCurrentStep] = useState<Step>(steps[currentStepIndex]);

    const prevStepIndexRef = useRef<number | null>(null);

    // const currentStep = steps[currentStepIndex];

    const isFirstStep = 0 === currentStepIndex;
    const isLastStep = currentStepIndex === steps.length - 1;

    const goToPreviousStep = () => {
        if (isFirstStep) {
            return;
        }

        const previousStepIndex = currentStepIndex - 1;
        const prevStep = steps[previousStepIndex];

        if (currentStep.onBeforeStepChange) {
            try {
                currentStep.onBeforeStepChange(currentStep, prevStep);
            } catch (e) {
                return;
            }
        }

        prevStepIndexRef.current = currentStepIndex;

        setCurrentStepIndex(previousStepIndex);
    };

    const goToNextStep = () => {
        if (isLastStep) {
            return;
        }

        const nextStepIndex = currentStepIndex + 1;
        const nextStep = steps[nextStepIndex];

        if (currentStep.onBeforeStepChange) {
            try {
                currentStep.onBeforeStepChange(currentStep, nextStep);
            } catch (e) {
                return;
            }
        }

        prevStepIndexRef.current = currentStepIndex;

        setCurrentStepIndex(nextStepIndex);
    };

    const goToStep = (stepIndex: number) => {
        if (stepIndex < 0 || stepIndex > steps.length - 1) {
            return;
        }

        const nextStep = steps[stepIndex];

        if (currentStep.onBeforeStepChange) {
            try {
                currentStep.onBeforeStepChange(currentStep, nextStep);
            } catch (e) {
                return;
            }
        }

        prevStepIndexRef.current = currentStepIndex;

        setCurrentStepIndex(stepIndex);
    };

    const completeFinalStep = () => {
        if (currentStep.onBeforeStepChange) {
            try {
                currentStep.onBeforeStepChange(currentStep, null);
            } catch (e) {
                return;
            }
        }

        if (!onFinalStepChange) {
            return;
        }

        onFinalStepChange(currentStep);
    };

    useEffect(() => {
        setCurrentStep(steps[currentStepIndex]);
    }, [currentStepIndex, steps]);

    useEffect(() => {
        if (onStepChange) {
            onStepChange(currentStep);
        }

        if (null !== prevStepIndexRef.current) {
            steps[prevStepIndexRef.current]
                .onAfterStepChange?.(
                currentStep,
                steps[prevStepIndexRef.current]
            );

            prevStepIndexRef.current = null;
        }
    }, [currentStep, onStepChange, steps]);

    return {
        currentStep,
        currentStepIndex,
        isFirstStep,
        isLastStep,
        isValidStep,
        goToPreviousStep,
        goToNextStep,
        goToStep,
        completeFinalStep,
        setIsValidStep
    };
};

export default useStepper;